import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { User, Settings, Camera, Lock, Save, AlertCircle, Trash2, Sun, Moon, Mail, Rss } from 'lucide-react';
import { api } from '../App';
import toast from 'react-hot-toast';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '@shared/theme/ThemeContext';
import MobileMenuPage from '../components/MobileMenuPage';
import Tabs from '@shared/ui/Tabs';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import Button from '@shared/ui/Button';
import { UploadIcon } from '../components/icons';
import AddressAutocomplete, { type AddressParts } from '../components/AddressAutocomplete';
import InterestsPicker from '../components/InterestsPicker';
import NewsSourcesSection from '../components/profile/NewsSourcesSection';
import { newPasswordsMatch, passwordChangeFields, PASSWORD_HINT } from '../components/auth/credentialSchema';

// Profile form schema
const profileSchema = z.object({
  username: z.string().optional(),
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  phone: z.string().optional(),
  occupation: z.string().optional(),
  education: z.string().optional(),
  interests: z.string().optional(),
  bio: z.string().optional(),
});

// Password change schema — rules from components/auth/credentialSchema
const passwordSchema = z
  .object(passwordChangeFields)
  .refine(newPasswordsMatch.check, newPasswordsMatch.options);

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

/**
 * One toggle per notification type — mirrors the NOTIFICATION_TYPES registry
 * in backend/src/services/notificationService.js. Every type the backend
 * writes must appear here so users can turn it off (all default on).
 */
const NOTIFICATION_PREF_ITEMS = [
  {
    key: 'bill_added_to_workspace',
    title: 'Bill added to a workspace',
    description: 'Notify when a bill is added to a workspace you are a member of',
  },
  {
    key: 'calendar_event_created',
    title: 'New calendar events (tracked bills)',
    description: 'Notify when a new calendar event is added for a bill you track',
  },
  {
    key: 'workspace_invitation',
    title: 'Workspace invitations',
    description: 'Notify when you receive a workspace invitation',
  },
  {
    key: 'note_created',
    title: 'Note added to a workspace',
    description: 'Notify when a new note is created in a workspace you are a member of',
  },
  {
    key: 'note_shared',
    title: 'Notes shared with you',
    description: 'Notify when someone shares a note with you and requests feedback',
  },
  {
    key: 'outreach_response_requested',
    title: 'Outreach responses requested',
    description: 'Notify when an outreach report is shared with you requesting a response',
  },
  {
    key: 'outreach_recipient_responded',
    title: 'Outreach recipient responses',
    description: 'Notify when a recipient responds to outreach results you shared',
  },
  {
    key: 'outreach_update',
    title: 'Outreach updates',
    description: 'Notify when an organization posts an update on outreach you responded to',
  },
  {
    key: 'system_announcements',
    title: 'System announcements',
    description: 'Notify about operational updates from Capitol Commons (for example calendar delays)',
  },
];

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

/** Tab set for the profile page. Ids are stable — they appear in ?section=. */
const PROFILE_TABS = [
  { id: 'profile', label: 'Profile Information', icon: User },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'notifications', label: 'Notifications', icon: Settings },
  { id: 'news', label: 'News Sources', icon: Rss },
];

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const mobileSection = searchParams.get('section');

  // Theme context
  const { theme, setTheme } = useTheme();

  // Set page title for mobile header
  usePageTitle('User Profile', 'Manage your account settings');

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch current user profile data
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["/users/profile"],
    queryFn: async () => {
      const response = await api.get("/users/profile");
      return response.data.data;
    },
    enabled: !!currentUser.id,
  });

  // Direct fetch function for profile data (bypasses cache like organizations do)
  const fetchProfileData = async () => {
    try {
      const response = await api.get("/users/profile");
      // Update the query cache with fresh data
      queryClient.setQueryData(["/users/profile"], response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      throw error;
    }
  };

  // Profile form setup
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: "",
      displayName: "",
      firstName: "",
      lastName: "",
      email: "",
      dateOfBirth: "",
      gender: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      occupation: "",
      education: "",
      interests: "",
      bio: "",
    },
  });

  /*
    Picking an address suggestion fills City, State and ZIP from the same place,
    rather than leaving the user to retype what they just selected (issue #1162).
    Only fields Google actually resolved are written, so a partial match cannot
    blank out something already entered. Each is marked dirty so the form knows
    it has unsaved changes.
  */
  const handleAddressSelect = (parts: AddressParts) => {
    const filled: Array<[keyof ProfileFormData, string]> = [
      ['city', parts.city],
      ['state', parts.state],
      ['zipCode', parts.zip],
    ];

    filled.forEach(([field, value]) => {
      if (value) profileForm.setValue(field, value, { shouldDirty: true });
    });
  };

  // Password form setup
  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Update profile form when data loads
  useEffect(() => {
    if (profileData && !profileForm.formState.isDirty) {
      profileForm.reset({
        username: (profileData as any).username || "",
        displayName: (profileData as any).displayName || "",
        firstName: (profileData as any).firstName || "",
        lastName: (profileData as any).lastName || "",
        email: (profileData as any).email || "",
        dateOfBirth: (profileData as any).dateOfBirth || "",
        gender: (profileData as any).gender || "",
        address: (profileData as any).address || "",
        city: (profileData as any).city || "",
        state: (profileData as any).state || "",
        zipCode: (profileData as any).zipCode || "",
        phone: (profileData as any).phone || "",
        occupation: (profileData as any).occupation || "",
        education: (profileData as any).education || "",
        interests: (profileData as any).interests || "",
        bio: (profileData as any).bio || "",
      });
    }
  }, [profileData, profileForm]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    // Simple toast implementation
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 px-4 py-2 rounded-lg text-white z-50 ${type === 'success' ? 'bg-green-500' : 'bg-red-500'
      }`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 3000);
  };

  // Notifications preferences
  const { data: notificationPrefsData, isLoading: prefsLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const response = await api.get('/notifications/preferences');
      return response.data.data.preferences;
    },
    enabled: !!currentUser.id,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const updateNotificationPrefs = useMutation({
    mutationFn: async (updates: any) => {
      const response = await api.put('/notifications/preferences', updates);
      return response.data.data.preferences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Notification preferences updated');
    },
    onError: () => {
      toast.error('Failed to update notification preferences');
    },
  });

  // Calendar email subscription
  const { data: calendarSubData, isLoading: calendarSubLoading, refetch: refetchCalendarSub } = useQuery({
    queryKey: ['calendar-email-subscription'],
    queryFn: async () => {
      const res = await api.get('/calendar-email-subscriptions/my-subscription');
      return res.data.data;
    },
    enabled: !!currentUser.id,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // State for workspace selection UI
  const [calendarIncludeAll, setCalendarIncludeAll] = useState(true);
  const [calendarSelectedWorkspaces, setCalendarSelectedWorkspaces] = useState<string[]>([]);

  // Sync state when data loads
  useEffect(() => {
    if (calendarSubData) {
      setCalendarIncludeAll(calendarSubData.includeAllWorkspaces ?? true);
      setCalendarSelectedWorkspaces(
        calendarSubData.selectedWorkspaces?.map((ws: any) => ws.id) || []
      );
    }
  }, [calendarSubData]);

  const calendarSubscribeMutation = useMutation({
    mutationFn: async (data: { includeAllWorkspaces: boolean; workspaceIds: string[] }) => {
      const response = await api.post('/calendar-email-subscriptions/subscribe', data);
      return response.data;
    },
    onSuccess: () => {
      refetchCalendarSub();
      toast.success('Subscribed to calendar digest emails');
    },
    onError: () => {
      toast.error('Failed to subscribe');
    },
  });

  const calendarUnsubscribeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/calendar-email-subscriptions/unsubscribe', {});
      return response.data;
    },
    onSuccess: () => {
      refetchCalendarSub();
      toast.success('Unsubscribed from calendar digest emails');
    },
    onError: () => {
      toast.error('Failed to unsubscribe');
    },
  });

  const calendarUpdatePrefsMutation = useMutation({
    mutationFn: async (data: { includeAllWorkspaces: boolean; workspaceIds: string[] }) => {
      const response = await api.put('/calendar-email-subscriptions/preferences', data);
      return response.data;
    },
    onSuccess: () => {
      refetchCalendarSub();
      toast.success('Preferences updated');
    },
    onError: () => {
      toast.error('Failed to update preferences');
    },
  });

  // Helper for calendar subscription
  const hasMultipleWorkspaces = (calendarSubData?.availableWorkspaces?.length || 0) > 1;
  const singleWorkspaceName = calendarSubData?.availableWorkspaces?.[0]?.name || '';

  const handleCalendarSubscribeToggle = (checked: boolean) => {
    if (checked) {
      // Subscribe
      calendarSubscribeMutation.mutate({
        includeAllWorkspaces: calendarIncludeAll,
        workspaceIds: calendarIncludeAll ? [] : calendarSelectedWorkspaces,
      });
    } else {
      // Unsubscribe
      calendarUnsubscribeMutation.mutate();
    }
  };

  const handleCalendarPrefsChange = (includeAll: boolean, workspaceIds: string[]) => {
    setCalendarIncludeAll(includeAll);
    setCalendarSelectedWorkspaces(workspaceIds);
    
    // Only update if already subscribed
    if (calendarSubData?.isSubscribed) {
      calendarUpdatePrefsMutation.mutate({
        includeAllWorkspaces: includeAll,
        workspaceIds: includeAll ? [] : workspaceIds,
      });
    }
  };

  // Send email verification link
  const sendVerificationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/send-verification');
      return response.data;
    },
    onSuccess: () => {
      toast.success('Verification email sent');
      queryClient.invalidateQueries({ queryKey: ["/users/profile"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to send verification email');
    },
  });

  // Profile update mutation
  const profileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const response = await api.put("/users/profile", data);
      return response.data;
    },
    onSuccess: () => {
      showToast('Profile updated successfully');
      queryClient.invalidateQueries({ queryKey: ["/users/profile"] });
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Failed to update profile', 'error');
    },
  });

  // Password update mutation
  const passwordMutation = useMutation({
    mutationFn: async (data: PasswordFormData) => {
      const response = await api.put("/users/password", data);
      return response.data;
    },
    onSuccess: () => {
      showToast('Password updated successfully');
      passwordForm.reset();
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Failed to update password', 'error');
    },
  });

  // Handle profile image upload (matches organization pattern - direct async, no mutations)
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/svg+xml", "image/webp"];
    if (!validTypes.includes(file.type)) {
      showToast("Please select a PNG, JPG, JPEG, SVG, or WebP image", "error");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      showToast("Please select an image smaller than 5MB", "error");
      return;
    }

    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await api.post('/users/profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showToast('Profile image updated successfully');
      
      // Refresh profile data to get the new image URL (like organizations do)
      await fetchProfileData();
      
      // Update localStorage for header avatar
      const uploadedImageUrl = response.data?.data?.profileImage?.url;
      if (uploadedImageUrl) {
        try {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          userData.profileImageUrl = uploadedImageUrl;
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
          console.warn('Failed to update localStorage with new profile image URL:', e);
        }
      }

      // Dispatch custom event to update header avatar
      window.dispatchEvent(new Event('profileImageUpdated'));
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to upload image';
      showToast(message, 'error');
    } finally {
      setIsUploadingImage(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  // Handle profile image delete (matches organization pattern - direct async, no mutations)
  const handleImageDelete = async () => {
    try {
      setIsDeletingImage(true);
      await api.delete('/users/profile-image');
      
      showToast('Profile photo deleted successfully');
      
      // Refresh profile data (like organizations do)
      await fetchProfileData();

      // Clear profile image from localStorage
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        delete userData.profileImageUrl;
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (e) {
        console.warn('Failed to update localStorage after profile image delete:', e);
      }

      // Dispatch custom event to update header avatar
      window.dispatchEvent(new Event('profileImageUpdated'));
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to delete photo';
      showToast(message, 'error');
    } finally {
      setIsDeletingImage(false);
    }
  };

  // Handle profile form submission
  const onProfileSubmit = async (data: ProfileFormData) => {
    // Just update profile data - image upload is handled separately via handleImageUpload
    profileMutation.mutate(data);
  };

  // Handle password form submission
  const onPasswordSubmit = (data: PasswordFormData) => {
    passwordMutation.mutate(data);
  };

  if (profileLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" label="Loading your profile…" />
      </div>
    );
  }

  // MOBILE: Show menu or section content
  if (isMobile) {
    // If no section param, show menu page
    if (!mobileSection) {
      const menuItems = [
        {
          id: 'profile',
          label: 'Profile Information',
          icon: User,
          route: '/profile?section=profile',
          description: 'Update your personal details and account information'
        },
        {
          id: 'security',
          label: 'Security',
          icon: Lock,
          route: '/profile?section=security',
          description: 'Change your password and security settings'
        },
        {
          id: 'notifications',
          label: 'Notifications',
          icon: Settings,
          route: '/profile?section=notifications',
          description: 'Manage your notification preferences'
        }
      ];

      return (
        <div className="px-4 pt-2">
          {/* Mobile Menu */}
          <MobileMenuPage menuItems={menuItems} />
        </div>
      );
    }

    // If has section param, show that section's content with back button
    // Update activeTab to match mobileSection
    if (mobileSection && ['profile', 'security', 'notifications'].includes(mobileSection)) {
      if (activeTab !== mobileSection) {
        setActiveTab(mobileSection);
      }

      return (
        <div className="px-4 space-y-4">
            {/* Show content based on section - same as desktop activeTab logic below */}
            {mobileSection === 'profile' && (
              <div className="space-y-4">
                  {/* Email verification status - only show when not verified */}
                  {profileData && !profileData.emailVerified && (
                    <div className="card">
                      <div className="px-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              Email not verified
                            </p>
                            <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                              Please verify your primary email address so we can send you important updates and notifications.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => sendVerificationMutation.mutate()}
                          disabled={sendVerificationMutation.isLoading}
                          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {sendVerificationMutation.isLoading ? 'Sending…' : 'Send verification email'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Profile Photo Card */}
                  <div className="card">
                    <div className="flex items-center space-x-2 mb-4">
                      <Camera className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Profile Photo</h3>
                    </div>
                    <p className="text-text-secondary dark:text-text-dark-secondary mb-4">Upload a profile photo to personalize your account</p>
                    
                    <div className="flex items-start gap-6">
                      {/* Photo Section - Vertical layout like Organization Settings */}
                      <div className="flex-shrink-0">
                        <div className="relative group">
                          {profileData?.profileImageUrl ? (
                            <div className="relative">
                              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border-light dark:border-border-dark">
                                {isUploadingImage || isDeletingImage ? (
                                  <div className="flex items-center justify-center w-full h-full bg-background-tertiary dark:bg-background-dark-tertiary">
                                    <LoadingSpinner size="md" />
                                  </div>
                                ) : (
                                  <img
                                    src={profileData.profileImageUrl}
                                    alt="Profile"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                              {/* Delete button overlay */}
                              {!isUploadingImage && !isDeletingImage && (
                                <button
                                  onClick={handleImageDelete}
                                  disabled={isDeletingImage}
                                  className="absolute -top-2 -right-2 p-1.5 bg-error-600 hover:bg-error-700 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-colors"
                                  title="Delete photo"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-full bg-background-tertiary dark:bg-background-dark-tertiary border-2 border-dashed border-border-light dark:border-border-dark flex items-center justify-center">
                              {isUploadingImage ? (
                                <LoadingSpinner size="md" />
                              ) : (
                                <User className="w-10 h-10 text-text-tertiary dark:text-text-dark-tertiary" />
                              )}
                            </div>
                          )}

                          {/* Upload Button */}
                          <label className="mt-3 inline-flex items-center justify-center gap-2 font-medium rounded-lg whitespace-nowrap transition-colors shadow-sm px-3 py-1.5 text-sm bg-primary-700 text-white hover:bg-primary-800 cursor-pointer">
                            <UploadIcon className="w-4 h-4" aria-hidden="true" />
                            {isUploadingImage ? 'Uploading…' : 'Upload'}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                              onChange={handleImageUpload}
                              disabled={isUploadingImage || isDeletingImage}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 text-center">
                            JPG, PNG or GIF. Max 5MB.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Theme Toggle Card - Mobile Only */}
                  <div className="card">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-1">Display Mode</h3>
                        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Choose your preferred color theme</p>
                      </div>
                      <div className="flex bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-1 gap-1">
                        <button
                          onClick={() => setTheme('light')}
                          className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all ${
                            theme === 'light'
                              ? 'bg-white dark:bg-background-dark-secondary shadow-sm text-primary-700 dark:text-primary-400'
                              : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
                          }`}
                          aria-label="Switch to light mode"
                        >
                          <Sun className="w-5 h-5" />
                          <span className="text-sm font-medium">Light</span>
                        </button>
                        <button
                          onClick={() => setTheme('dark')}
                          className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all ${
                            theme === 'dark'
                              ? 'bg-white dark:bg-background-dark-secondary shadow-sm text-primary-700 dark:text-primary-400'
                              : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
                          }`}
                          aria-label="Switch to dark mode"
                        >
                          <Moon className="w-5 h-5" />
                          <span className="text-sm font-medium">Dark</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information Card */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">Personal Information</h3>
                    <p className="text-text-secondary dark:text-text-dark-secondary mb-6">Update your personal details and contact information</p>

                    <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                      {/* Basic Information */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Username</label>
                          <input
                            {...profileForm.register('username')}
                            className="input"
                            placeholder="Enter username"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Display Name</label>
                          <input
                            {...profileForm.register('displayName')}
                            className="input"
                            placeholder="Enter display name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">First Name</label>
                          <input
                            {...profileForm.register('firstName')}
                            className="input"
                            placeholder="Enter first name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Last Name</label>
                          <input
                            {...profileForm.register('lastName')}
                            className="input"
                            placeholder="Enter last name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Email</label>
                          <input
                            {...profileForm.register('email')}
                            type="email"
                            className="input"
                            placeholder="Enter email address"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Date of Birth</label>
                          <input
                            {...profileForm.register('dateOfBirth')}
                            type="date"
                            className="input"
                          />
                        </div>
                      </div>

                      {/* Demographics */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Demographics</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Gender</label>
                            <select
                              {...profileForm.register('gender')}
                              className="input"
                            >
                              <option value="">Select gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="non-binary">Non-binary</option>
                              <option value="prefer-not-to-say">Prefer not to say</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Phone Number</label>
                            <input
                              {...profileForm.register('phone')}
                              className="input"
                              placeholder="Enter phone number"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Address Information */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Address Information</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Street Address</label>
                            <Controller
                              name="address"
                              control={profileForm.control}
                              render={({ field }) => (
                                <AddressAutocomplete
                                  value={field.value || ''}
                                  onChange={field.onChange}
                                  onSelect={handleAddressSelect}
                                />
                              )}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">City</label>
                              <input
                                {...profileForm.register('city')}
                                className="input"
                                placeholder="Enter city"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">State</label>
                              <select
                                {...profileForm.register('state')}
                                className="input"
                              >
                                <option value="">Select state</option>
                                {US_STATES.map((state) => (
                                  <option key={state.value} value={state.value}>
                                    {state.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">ZIP Code</label>
                              <input
                                {...profileForm.register('zipCode')}
                                className="input"
                                placeholder="Enter ZIP code"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Professional Information */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Professional Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Occupation</label>
                            <input
                              {...profileForm.register('occupation')}
                              className="input"
                              placeholder="Enter occupation"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Education</label>
                            <select
                              {...profileForm.register('education')}
                              className="input"
                            >
                              <option value="">Select education level</option>
                              <option value="No formal education">No formal education</option>
                              <option value="Some high school">Some high school</option>
                              <option value="High school diploma or equivalent (GED)">High school diploma or equivalent (GED)</option>
                              <option value="Some college, no degree">Some college, no degree</option>
                              <option value="Associate degree">Associate degree</option>
                              <option value="Bachelor's degree">Bachelor&apos;s degree</option>
                              <option value="Master's degree">Master&apos;s degree</option>
                              <option value="Professional degree">Professional degree</option>
                              <option value="Doctorate (PhD)">Doctorate (PhD)</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Interests and Bio */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Additional Information</h4>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Interests</label>
                          <Controller
                            name="interests"
                            control={profileForm.control}
                            render={({ field }) => (
                              <InterestsPicker value={field.value || ''} onChange={field.onChange} />
                            )}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Biography</label>
                          <textarea
                            {...profileForm.register('bio')}
                            className="input min-h-[100px]"
                            placeholder="Tell us about yourself and your civic engagement interests"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button type="submit" variant="primary" size="sm" icon={Save} loading={profileMutation.isLoading}>
                          {profileMutation.isLoading ? 'Saving…' : 'Save profile'}
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {mobileSection === 'security' && (
                <div className="card">
                  <div className="flex items-center space-x-2 mb-4">
                    <Lock className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Change Password</h3>
                  </div>
                  <p className="text-text-secondary dark:text-text-dark-secondary mb-6">Update your password to keep your account secure</p>

                  <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Current Password</label>
                      <input
                        {...passwordForm.register('currentPassword')}
                        type="password"
                        className="input"
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">New Password</label>
                      <input
                        {...passwordForm.register('newPassword')}
                        type="password"
                        className="input"
                        placeholder="Enter new password"
                      />
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        {PASSWORD_HINT}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Confirm New Password</label>
                      <input
                        {...passwordForm.register('confirmPassword')}
                        type="password"
                        className="input"
                        placeholder="Confirm new password"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={passwordMutation.isLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 disabled:opacity-50"
                      >
                        <Lock className="h-4 w-4" />
                        <span>{passwordMutation.isLoading ? "Updating..." : "Update password"}</span>
                      </button>
                    </div>
                  </form>
                </div>
              )}
              {mobileSection === 'notifications' && (
                <div className="space-y-6">
                  {/* In-App Notifications */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                      Notification Preferences
                    </h3>
                    <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                      Choose which events generate in-app notifications.
                    </p>

                    {prefsLoading ? (
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary">Loading preferences...</div>
                    ) : (
                      <div className="space-y-4">
                        {NOTIFICATION_PREF_ITEMS.map((item) => (
                          <div key={item.key} className="card p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                                  {item.title}
                                </div>
                                <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                                  {item.description}
                                </div>
                              </div>
                              <label className="flex items-center gap-2">
                                <span className="sr-only">{item.title}</span>
                                <input
                                  type="checkbox"
                                  checked={!!(notificationPrefsData as any)?.[item.key]}
                                  onChange={(e) => updateNotificationPrefs.mutate({ [item.key]: e.target.checked })}
                                  className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                                />
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Email Subscriptions */}
                  <div className="card">
                    <div className="flex items-center space-x-2 mb-2">
                      <Mail className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                        Email Subscriptions
                      </h3>
                    </div>
                    <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                      Manage which emails you receive from Capitol Commons.
                    </p>

                    {calendarSubLoading ? (
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary">Loading subscription status...</div>
                    ) : (
                      <div className="space-y-4">
                        {/* Calendar Digest Toggle */}
                        <div className="card p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                                Calendar Digest Emails
                              </div>
                              <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                                {hasMultipleWorkspaces
                                  ? "Receive a nightly email with the next day's legislative calendar for your tracked bills"
                                  : `Receive a nightly email with the next day's legislative calendar for ${singleWorkspaceName || 'your workspace'}`
                                }
                              </div>
                            </div>
                            <label className="flex items-center gap-2">
                              <span className="sr-only">Calendar Digest Emails</span>
                              <input
                                type="checkbox"
                                checked={calendarSubData?.isSubscribed || false}
                                onChange={(e) => handleCalendarSubscribeToggle(e.target.checked)}
                                disabled={calendarSubscribeMutation.isLoading || calendarUnsubscribeMutation.isLoading}
                                className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                              />
                            </label>
                          </div>

                          {/* Workspace Selection (only if subscribed AND has multiple workspaces) */}
                          {calendarSubData?.isSubscribed && hasMultipleWorkspaces && (
                            <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                              <div className="space-y-3">
                                {/* Include All Toggle */}
                                <label className="flex items-center gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={calendarIncludeAll}
                                    onChange={(e) => handleCalendarPrefsChange(e.target.checked, calendarSelectedWorkspaces)}
                                    disabled={calendarUpdatePrefsMutation.isLoading}
                                    className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                                  />
                                  <span className="text-sm text-text-primary dark:text-text-dark-primary">
                                    Include all my workspaces
                                  </span>
                                </label>

                                {/* Workspace Checkboxes (only if not including all) */}
                                {!calendarIncludeAll && (
                                  <div className="ml-7 space-y-2">
                                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary mb-2">
                                      Select which workspaces to include:
                                    </div>
                                    {calendarSubData?.availableWorkspaces?.map((ws: any) => (
                                      <label key={ws.id} className="flex items-center gap-3 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={calendarSelectedWorkspaces.includes(ws.id)}
                                          onChange={(e) => {
                                            const newSelection = e.target.checked
                                              ? [...calendarSelectedWorkspaces, ws.id]
                                              : calendarSelectedWorkspaces.filter(id => id !== ws.id);
                                            handleCalendarPrefsChange(false, newSelection);
                                          }}
                                          disabled={calendarUpdatePrefsMutation.isLoading}
                                          className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm text-text-primary dark:text-text-dark-primary">
                                          {ws.name}
                                          {ws.organization_name && (
                                            <span className="text-text-tertiary dark:text-text-dark-tertiary ml-1">
                                              ({ws.organization_name})
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
        </div>
      );
    }

    // Invalid section param, redirect to menu
    navigate('/profile');
  }

  // DESKTOP: Original tab-based navigation (UNCHANGED)
  return (
    <div className="space-y-6">
        {/* Title renders in Layout's title bar (usePageTitle above) — no body heading. */}
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div>
            <Tabs
              level="section"
              ariaLabel="Profile sections"
              tabs={PROFILE_TABS}
              activeId={activeTab}
              onChange={setActiveTab}
            />
          </div>
          {/* Profile Tab Content */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Email verification status - only show when not verified */}
              {profileData && !profileData.emailVerified && (
                <div className="card">
                  <div className="px-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          Email not verified
                        </p>
                        <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                          Please verify your primary email address so we can send you important updates and notifications.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => sendVerificationMutation.mutate()}
                      disabled={sendVerificationMutation.isLoading}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {sendVerificationMutation.isLoading ? 'Sending…' : 'Send verification email'}
                    </button>
                  </div>
                </div>
              )}

              {/* Profile Photo Card */}
              <div className="card">
                <div className="flex items-center space-x-2 mb-4">
                  <Camera className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Profile Photo</h3>
                </div>
                <p className="text-text-secondary dark:text-text-dark-secondary mb-4">Upload a profile photo to personalize your account</p>
                
                <div className="flex items-start gap-6">
                  {/* Photo Section - Vertical layout like Organization Settings */}
                  <div className="flex-shrink-0">
                    <div className="relative group">
                      {profileData?.profileImageUrl ? (
                        <div className="relative">
                          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border-light dark:border-border-dark">
                            {isUploadingImage || isDeletingImage ? (
                              <div className="flex items-center justify-center w-full h-full bg-background-tertiary dark:bg-background-dark-tertiary">
                                <LoadingSpinner size="md" />
                              </div>
                            ) : (
                              <img
                                src={profileData.profileImageUrl}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          {/* Delete button overlay */}
                          {!isUploadingImage && !isDeletingImage && (
                            <button
                              onClick={handleImageDelete}
                              disabled={isDeletingImage}
                              className="absolute -top-2 -right-2 p-1.5 bg-error-600 hover:bg-error-700 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-colors"
                              title="Delete photo"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-background-tertiary dark:bg-background-dark-tertiary border-2 border-dashed border-border-light dark:border-border-dark flex items-center justify-center">
                          {isUploadingImage ? (
                            <LoadingSpinner size="md" />
                          ) : (
                            <User className="w-10 h-10 text-text-tertiary dark:text-text-dark-tertiary" />
                          )}
                        </div>
                      )}

                      {/* Upload Button */}
                      <label className="mt-3 inline-flex items-center justify-center gap-2 font-medium rounded-lg whitespace-nowrap transition-colors shadow-sm px-3 py-1.5 text-sm bg-primary-700 text-white hover:bg-primary-800 cursor-pointer">
                        <UploadIcon className="w-4 h-4" aria-hidden="true" />
                        {isUploadingImage ? 'Uploading…' : 'Upload'}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                          onChange={handleImageUpload}
                          disabled={isUploadingImage || isDeletingImage}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 text-center">
                        JPG, PNG or GIF. Max 5MB.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information Card */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">Personal Information</h3>
                <p className="text-text-secondary dark:text-text-dark-secondary mb-6">Update your personal details and contact information</p>

                <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Username</label>
                      <input
                        {...profileForm.register('username')}
                        className="input"
                        placeholder="Enter username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Display Name</label>
                      <input
                        {...profileForm.register('displayName')}
                        className="input"
                        placeholder="Enter display name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">First Name</label>
                      <input
                        {...profileForm.register('firstName')}
                        className="input"
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Last Name</label>
                      <input
                        {...profileForm.register('lastName')}
                        className="input"
                        placeholder="Enter last name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Email</label>
                      <input
                        {...profileForm.register('email')}
                        type="email"
                        className="input"
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Date of Birth</label>
                      <input
                        {...profileForm.register('dateOfBirth')}
                        type="date"
                        className="input"
                      />
                    </div>
                  </div>

                  {/* Demographics */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Demographics</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Gender</label>
                        <select
                          {...profileForm.register('gender')}
                          className="input"
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="non-binary">Non-binary</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Phone Number</label>
                        <input
                          {...profileForm.register('phone')}
                          className="input"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Address Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Street Address</label>
                        <Controller
                          name="address"
                          control={profileForm.control}
                          render={({ field }) => (
                            <AddressAutocomplete
                              value={field.value || ''}
                              onChange={field.onChange}
                              onSelect={handleAddressSelect}
                            />
                          )}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">City</label>
                          <input
                            {...profileForm.register('city')}
                            className="input"
                            placeholder="Enter city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">State</label>
                          <select
                            {...profileForm.register('state')}
                            className="input"
                          >
                            <option value="">Select state</option>
                            {US_STATES.map((state) => (
                              <option key={state.value} value={state.value}>
                                {state.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">ZIP Code</label>
                          <input
                            {...profileForm.register('zipCode')}
                            className="input"
                            placeholder="Enter ZIP code"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Professional Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Occupation</label>
                        <input
                          {...profileForm.register('occupation')}
                          className="input"
                          placeholder="Enter occupation"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Education</label>
                        <select
                          {...profileForm.register('education')}
                          className="input"
                        >
                          <option value="">Select education level</option>
                          <option value="No formal education">No formal education</option>
                          <option value="Some high school">Some high school</option>
                          <option value="High school diploma or equivalent (GED)">High school diploma or equivalent (GED)</option>
                          <option value="Some college, no degree">Some college, no degree</option>
                          <option value="Associate degree">Associate degree</option>
                          <option value="Bachelor's degree">Bachelor&apos;s degree</option>
                          <option value="Master's degree">Master&apos;s degree</option>
                          <option value="Professional degree">Professional degree</option>
                          <option value="Doctorate (PhD)">Doctorate (PhD)</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Interests and Bio */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Additional Information</h4>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Interests</label>
                      <Controller
                        name="interests"
                        control={profileForm.control}
                        render={({ field }) => (
                          <InterestsPicker value={field.value || ''} onChange={field.onChange} />
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Biography</label>
                      <textarea
                        {...profileForm.register('bio')}
                        className="input min-h-[100px]"
                        placeholder="Tell us about yourself and your civic engagement interests"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" variant="primary" size="sm" icon={Save} loading={profileMutation.isLoading}>
                      {profileMutation.isLoading ? 'Saving…' : 'Save profile'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Security Tab Content */}
          {activeTab === 'security' && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <Lock className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Change Password</h3>
              </div>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">Update your password to keep your account secure</p>

              <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Current Password</label>
                  <input
                    {...passwordForm.register('currentPassword')}
                    type="password"
                    className="input"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">New Password</label>
                  <input
                    {...passwordForm.register('newPassword')}
                    type="password"
                    className="input"
                    placeholder="Enter new password"
                  />
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                    {PASSWORD_HINT}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">Confirm New Password</label>
                  <input
                    {...passwordForm.register('confirmPassword')}
                    type="password"
                    className="input"
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={passwordMutation.isLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 disabled:opacity-50"
                  >
                    <Lock className="h-4 w-4" />
                    <span>{passwordMutation.isLoading ? "Updating..." : "Update password"}</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Notifications Tab Content */}
          {activeTab === 'news' && <NewsSourcesSection />}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              {/* In-App Notifications */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                  Notification Preferences
                </h3>
                <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                  Choose which events generate in-app notifications.
                </p>

                {prefsLoading ? (
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">Loading preferences...</div>
                ) : (
                  <div className="space-y-4">
                    {NOTIFICATION_PREF_ITEMS.map((item) => (
                      <div key={item.key} className="card p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {item.title}
                            </div>
                            <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                              {item.description}
                            </div>
                          </div>
                          <label className="flex items-center gap-2">
                            <span className="sr-only">{item.title}</span>
                            <input
                              type="checkbox"
                              checked={!!(notificationPrefsData as any)?.[item.key]}
                              onChange={(e) => updateNotificationPrefs.mutate({ [item.key]: e.target.checked })}
                              className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Email Subscriptions */}
              <div className="card">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                    Email Subscriptions
                  </h3>
                </div>
                <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                  Manage which emails you receive from Capitol Commons.
                </p>

                {calendarSubLoading ? (
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">Loading subscription status...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Calendar Digest Toggle */}
                    <div className="card p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                            Calendar Digest Emails
                          </div>
                          <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                            {hasMultipleWorkspaces
                              ? "Receive a nightly email with the next day's legislative calendar for your tracked bills"
                              : `Receive a nightly email with the next day's legislative calendar for ${singleWorkspaceName || 'your workspace'}`
                            }
                          </div>
                        </div>
                        <label className="flex items-center gap-2">
                          <span className="sr-only">Calendar Digest Emails</span>
                          <input
                            type="checkbox"
                            checked={calendarSubData?.isSubscribed || false}
                            onChange={(e) => handleCalendarSubscribeToggle(e.target.checked)}
                            disabled={calendarSubscribeMutation.isLoading || calendarUnsubscribeMutation.isLoading}
                            className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                          />
                        </label>
                      </div>

                      {/* Workspace Selection (only if subscribed AND has multiple workspaces) */}
                      {calendarSubData?.isSubscribed && hasMultipleWorkspaces && (
                        <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                          <div className="space-y-3">
                            {/* Include All Toggle */}
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={calendarIncludeAll}
                                onChange={(e) => handleCalendarPrefsChange(e.target.checked, calendarSelectedWorkspaces)}
                                disabled={calendarUpdatePrefsMutation.isLoading}
                                className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-text-primary dark:text-text-dark-primary">
                                Include all my workspaces
                              </span>
                            </label>

                            {/* Workspace Checkboxes (only if not including all) */}
                            {!calendarIncludeAll && (
                              <div className="ml-7 space-y-2">
                                <div className="text-xs text-text-secondary dark:text-text-dark-secondary mb-2">
                                  Select which workspaces to include:
                                </div>
                                {calendarSubData?.availableWorkspaces?.map((ws: any) => (
                                  <label key={ws.id} className="flex items-center gap-3 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={calendarSelectedWorkspaces.includes(ws.id)}
                                      onChange={(e) => {
                                        const newSelection = e.target.checked
                                          ? [...calendarSelectedWorkspaces, ws.id]
                                          : calendarSelectedWorkspaces.filter(id => id !== ws.id);
                                        handleCalendarPrefsChange(false, newSelection);
                                      }}
                                      disabled={calendarUpdatePrefsMutation.isLoading}
                                      className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-text-primary dark:text-text-dark-primary">
                                      {ws.name}
                                      {ws.organization_name && (
                                        <span className="text-text-tertiary dark:text-text-dark-tertiary ml-1">
                                          ({ws.organization_name})
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
    </div>
  );
} 